/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { useSetRecoilState } from "recoil";
import { ContactApi } from "../../../../api/Contacts";
import { ContactsAtom } from "../../../../atoms/ContactsAtom";
import { SpaceBetween } from "../../../../styles/general";
import { IContact } from "../../../../types/Contact";
import { ITag } from "../../../../types/TagTypes";
import LoadingAbsolute from "../../../LoadingAbsolute";
import StyledButton from "../../../StyledButton";
import TagSelect from "../../../TagSelect/TagSelect";
import { useModal } from "../../Modal";

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 400;
  margin-bottom: 5px;
`;

const TagsContainer = styled.div`
  width: 370px;
  margin-top: 20px;
  padding: 20px 20px 0px;
  border-top: 1px solid #eceeee;
  display: flex;
  flex-direction: column;
`;

const Buttons = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  border-top: 1px solid #eceeee;
  padding: 20px 20px 0px;
`;

interface Props {
  contact: IContact;
}

export default function ApplyTagModal(props: Props): JSX.Element {
  const { contact } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [addSelectedTags, setAddSelectedTags] = useState<ITag[]>([]);
  const setContacts = useSetRecoilState(ContactsAtom);

  const modal = useModal();

  async function handleClick() {
    setIsLoading(true);

    let newTags = addSelectedTags
      .filter((ts) => !props.contact.tags.find((ct) => ct._id === ts._id))
      .map((t) => {
        return {
          _id: t._id!,
          appliedAt: new Date(),
        };
      });

    let newContactTags = [...contact.tags, ...newTags];

    const response = await ContactApi.update(
      {
        ...(contact as any),
        tags: newContactTags,
      },
      contact._id
    );

    if (!response) {
      toast("Error on add tags on contact, try again.", {
        type: "error",
      });
      return;
    }

    setContacts((cs) =>
      cs
        ? cs.map((c) =>
            `${c._id}` === `${contact._id}`
              ? { ...contact, tags: newContactTags as any }
              : c
          )
        : []
    );

    setAddSelectedTags([]);
    setIsLoading(false);
    modal.popModal();
  }

  return (
    <>
      <TagsContainer>
        <SpaceBetween>
          <Title>Apply Tags</Title>
        </SpaceBetween>
        <div>
          <TagSelect
            tags={addSelectedTags}
            preserveSystemTags={true}
            handleChoseTags={(tags) => setAddSelectedTags(tags)}
          />
        </div>
      </TagsContainer>
      <Buttons>
        <StyledButton
          inverted
          css={css`
            width: 80px;
            margin-right: 10px;
          `}
          onClick={modal.popModal}
        >
          Cancel
        </StyledButton>
        <StyledButton
          css={css`
            width: 82px;
          `}
          isLoading={isLoading}
          onClick={handleClick}
        >
          Apply
        </StyledButton>
      </Buttons>

      {isLoading && <LoadingAbsolute />}
    </>
  );
}
